import { WebShellOptimization } from "@nike/web-shell-types";
import type {
  GetLocationsArgument,
  GetLocationsHandler,
  GetLocationsResult,
  Location
} from "@nike/web-shell-types";

const DEFAULT_QUEUE_TIMEOUT_MS = 5000;

interface QueuedRequest {
  argument?: GetLocationsArgument;
  resolve: (result: GetLocationsResult | undefined) => void;
}

export class DotcomWebShellQueueOptimization extends WebShellOptimization {
  private _queue: QueuedRequest[] = [];
  private _timeoutMs: number;
  private _timeoutId: ReturnType<typeof setTimeout> | null = null;
  private _handler: GetLocationsHandler | null = null;
  private _queueTimedOut = false;
  private _inflightMap = new Map<string, Promise<GetLocationsResult | undefined>>();

  constructor(locations?: Location[], timeoutMs: number = DEFAULT_QUEUE_TIMEOUT_MS) {
    super(locations);
    this._timeoutMs = timeoutMs;
  }

  private _getCacheKey = (arg?: GetLocationsArgument): string => {
    return JSON.stringify(arg ?? {});
  };

  public getLocations = (
    getLocationsArgument?: GetLocationsArgument
  ): Promise<GetLocationsResult | undefined> => {
    if (this._handler) {
      try {
        return this._handler(getLocationsArgument).catch(() => undefined);
      } catch {
        return Promise.resolve(undefined);
      }
    }

    // If queue timed out and no handler registered, return undefined
    if (this._queueTimedOut) {
      return Promise.resolve(undefined);
    }

    const cacheKey = this._getCacheKey(getLocationsArgument);
    if (this._inflightMap.has(cacheKey)) {
      return this._inflightMap.get(cacheKey)!;
    }

    const promise = new Promise<GetLocationsResult | undefined>((resolve) => {
      this._queue.push({
        argument: getLocationsArgument,
        resolve
      });

      if (this._timeoutId === null) {
        this._timeoutId = setTimeout(() => {
          this._drainQueueWithUndefined();
        }, this._timeoutMs);
      }
    });

    this._inflightMap.set(cacheKey, promise);
    promise.finally(() => {
      this._inflightMap.delete(cacheKey);
    });

    return promise;
  };

  private _drainQueueWithUndefined = (): void => {
    this._queueTimedOut = true;

    while (this._queue.length > 0) {
      const request = this._queue.shift();
      request?.resolve(undefined);
    }

    if (this._timeoutId !== null) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  };

  public setHandler = async (handler: GetLocationsHandler): Promise<void> => {
    if (this._handler) {
      return;
    }

    if (this._timeoutId !== null) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }

    this._handler = handler;

    while (this._queue.length > 0) {
      const request = this._queue.shift();
      if (request) {
        try {
          const result = await handler(request.argument);
          request.resolve(result);
        } catch {
          request.resolve(undefined);
        }
      }
    }
  };

  public get queueLength(): number {
    return this._queue.length;
  }

  public get hasHandler(): boolean {
    return this._handler !== null;
  }
}
